export type RetryPolicy = {
  maxAttempts: number;
  initialDelayMs: number;
  backoffFactor: number;
};

export const UNITY_LOAD_RETRY_POLICY: RetryPolicy = {
  maxAttempts: 3,
  initialDelayMs: 200,
  backoffFactor: 2,
};

/** Upper bound so a 60s rate-limit window cannot stall a game load. */
export const UNITY_LOAD_MAX_RETRY_AFTER_MS = 10_000;

export function parseRetryAfterMs(header: string | null | undefined): number | undefined {
  if (!header) {
    return undefined;
  }

  const trimmed = header.trim();
  if (!trimmed) {
    return undefined;
  }

  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.round(seconds * 1000);
  }

  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) {
    return undefined;
  }
  return Math.max(0, dateMs - Date.now());
}

function getErrorRetryAfterMs(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("retryAfterMs" in error)) {
    return undefined;
  }
  const value = (error as { retryAfterMs?: unknown }).retryAfterMs;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  return value;
}

function delayMsForRetry(error: unknown, backoffMs: number): number {
  const retryAfterMs = getErrorRetryAfterMs(error);
  if (retryAfterMs === undefined) {
    return backoffMs;
  }
  return Math.min(retryAfterMs, UNITY_LOAD_MAX_RETRY_AFTER_MS);
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

export function createAbortError(): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException("Aborted", "AbortError");
  }

  const error = new Error("Aborted");
  error.name = "AbortError";
  return error;
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timer);
      queueMicrotask(() => reject(createAbortError()));
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function retryOnTransientError<T>(
  operation: () => Promise<T>,
  options: {
    policy?: RetryPolicy;
    signal?: AbortSignal;
    sleep?: (ms: number) => Promise<void>;
    isRetryable: (error: unknown) => boolean;
  }
): Promise<T> {
  const policy = options.policy ?? UNITY_LOAD_RETRY_POLICY;
  const sleep = options.sleep ?? ((ms: number) => defaultSleep(ms, options.signal));
  const maxAttempts = Math.max(1, policy.maxAttempts);

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (options.signal?.aborted) {
      throw createAbortError();
    }

    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (isAbortError(error)) {
        throw error;
      }

      const hasAttemptsLeft = attempt < maxAttempts;
      if (!hasAttemptsLeft || !options.isRetryable(error)) {
        throw error;
      }

      const backoffMs = policy.initialDelayMs * policy.backoffFactor ** (attempt - 1);
      await sleep(delayMsForRetry(error, backoffMs));
    }
  }

  throw lastError;
}
